/**
 * PATH: packages/ui/src/config/unifiedHubConfig.ts
 * PURPOSE: Hub configuration for the unified cross-hub tab system.
 *
 * WHY: Cross-hub tabs need to know each hub's color, label, default path,
 *      and icon so the universal tab bar can render hub-tagged tabs.
 *
 * NOTE: This is separate from the existing hubRegistry.ts which handles
 *       external links and sidebar hub list. This file is specifically
 *       for the unified tab system's hub metadata.
 */

// ─── Hub Color Types ───────────────────────────────────────

export type HubColor =
  | 'teal'
  | 'blue'
  | 'purple'
  | 'orange'
  | 'emerald'
  | 'rose'
  | 'amber'
  | 'cyan'
  | 'indigo'
  | 'pink'
  | 'lime'
  | 'sky'
  | 'slate'

// ─── Hub Tab Config ────────────────────────────────────────

export interface HubTabConfig {
  /** Unique hub identifier (matches route prefix) */
  id: string
  /** Display label */
  label: string
  /** Short label for tab badges */
  shortLabel: string
  /** Accent color key */
  color: HubColor
  /** Default landing path within the hub (relative, no leading /) */
  defaultPath: string
  /** Tailwind bg class for the accent color */
  accentBg: string
  /** Tailwind text class */
  accentText: string
}

// ─── Hub Configs ───────────────────────────────────────────

export const UNIFIED_HUB_CONFIGS: Record<string, HubTabConfig> = {
  workforce: {
    id: 'workforce',
    label: 'Workforce',
    shortLabel: 'WF',
    color: 'teal',
    defaultPath: 'people',
    accentBg: 'bg-teal-500',
    accentText: 'text-teal-600 dark:text-teal-400',
  },
  crm: {
    id: 'crm',
    label: 'CRM',
    shortLabel: 'CRM',
    color: 'blue',
    defaultPath: 'quotes',
    accentBg: 'bg-blue-500',
    accentText: 'text-blue-600 dark:text-blue-400',
  },
  quote: {
    id: 'quote',
    label: 'Quote Builder',
    shortLabel: 'QB',
    color: 'purple',
    defaultPath: '',
    accentBg: 'bg-purple-500',
    accentText: 'text-purple-600 dark:text-purple-400',
  },
  qa: {
    id: 'qa',
    label: 'QA',
    shortLabel: 'QA',
    color: 'orange',
    defaultPath: '',
    accentBg: 'bg-orange-500',
    accentText: 'text-orange-600 dark:text-orange-400',
  },
  cfo: {
    id: 'cfo',
    label: 'CFO',
    shortLabel: 'CFO',
    color: 'emerald',
    defaultPath: '',
    accentBg: 'bg-emerald-500',
    accentText: 'text-emerald-600 dark:text-emerald-400',
  },
  qdrive: {
    id: 'qdrive',
    label: 'QDrive',
    shortLabel: 'QD',
    color: 'cyan',
    defaultPath: 'dashboard',
    accentBg: 'bg-cyan-500',
    accentText: 'text-cyan-600 dark:text-cyan-400',
  },
  email: {
    id: 'email',
    label: 'Email',
    shortLabel: 'EM',
    color: 'indigo',
    defaultPath: '',
    accentBg: 'bg-indigo-500',
    accentText: 'text-indigo-600 dark:text-indigo-400',
  },
  hr: {
    id: 'hr',
    label: 'HR',
    shortLabel: 'HR',
    color: 'rose',
    defaultPath: '',
    accentBg: 'bg-rose-500',
    accentText: 'text-rose-600 dark:text-rose-400',
  },
  teams: {
    id: 'teams',
    label: 'Teams',
    shortLabel: 'TM',
    color: 'amber',
    defaultPath: '',
    accentBg: 'bg-amber-500',
    accentText: 'text-amber-600 dark:text-amber-400',
  },
  ai: {
    id: 'ai',
    label: 'AI Hub',
    shortLabel: 'AI',
    color: 'pink',
    defaultPath: '',
    accentBg: 'bg-pink-500',
    accentText: 'text-pink-600 dark:text-pink-400',
  },
  billing: {
    id: 'billing',
    label: 'Billing',
    shortLabel: 'BL',
    color: 'lime',
    defaultPath: '',
    accentBg: 'bg-lime-500',
    accentText: 'text-lime-600 dark:text-lime-400',
  },
  dashboard: {
    id: 'dashboard',
    label: 'Dashboard',
    shortLabel: 'DB',
    color: 'slate',
    defaultPath: 'dashboard',
    accentBg: 'bg-slate-600',
    accentText: 'text-slate-600 dark:text-slate-400',
  },
}

// ─── Utility Functions ─────────────────────────────────────

/** Get hub config by ID */
export function getHubTabConfig(hubId: string): HubTabConfig | undefined {
  return UNIFIED_HUB_CONFIGS[hubId]
}

/** Extract hub ID from a URL path (e.g., '/workforce/people' → 'workforce') */
export function getHubIdFromPath(path: string): string {
  const segments = path.replace(/^\//, '').split('/')
  const candidate = segments[0]
  return UNIFIED_HUB_CONFIGS[candidate] ? candidate : 'dashboard'
}

/** Get the hub-relative path (e.g., '/workforce/people/123' → '/people/123') */
export function getHubRelativePath(fullPath: string, hubId: string): string {
  const prefix = `/${hubId}`
  if (fullPath.startsWith(prefix)) {
    const relative = fullPath.slice(prefix.length)
    return relative || '/'
  }
  return fullPath
}

/** Build a full hub path */
export function buildHubPath(hubId: string, relativePath: string): string {
  const clean = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
  return hubId === 'dashboard' ? clean : `/${hubId}${clean}`
}

/**
 * Set of hub IDs that have been consolidated into the unified shell.
 * These navigate internally via React Router instead of external redirects.
 * Add hub IDs here as they're migrated.
 *
 * `qdrive` (added 2026-06-16): the QDrive module is fully wired for in-shell
 * rendering — HubLoader registers `/qdrive/*` (HUB_ROUTES), `src/hubs/registry.ts`
 * maps `qdrive` to the real `@hub/qdrive` component, and `hubNavRegistry.ts`
 * has a `qdrive` case — AND the master `useHubTabStore` already lists qdrive
 * as consolidated. This set was the LAST place still omitting it, which made
 * `/qdrive` render BLANK (the unified-layout URL→hub sync uses isConsolidatedHub
 * here, so a qdrive tab never opened). Including it closes that gap so the
 * universal `/qdrive` rail item renders in-shell across all tenants.
 */
export const CONSOLIDATED_HUB_IDS = new Set(['workforce', 'crm', 'qdrive'])

/** Check if a hub has been consolidated into the unified shell */
export function isConsolidatedHub(hubId: string): boolean {
  return CONSOLIDATED_HUB_IDS.has(hubId)
}

/** Hub color → Tailwind CSS classes */
export const HUB_COLOR_CLASSES: Record<HubColor, { bg: string; text: string; dot: string; border: string; ring: string }> = {
  teal:    { bg: 'bg-teal-500',    text: 'text-teal-600 dark:text-teal-400',       dot: 'bg-teal-400',    border: 'border-teal-500',    ring: 'ring-teal-500/20' },
  blue:    { bg: 'bg-blue-500',    text: 'text-blue-600 dark:text-blue-400',       dot: 'bg-blue-400',    border: 'border-blue-500',    ring: 'ring-blue-500/20' },
  purple:  { bg: 'bg-purple-500',  text: 'text-purple-600 dark:text-purple-400',   dot: 'bg-purple-400',  border: 'border-purple-500',  ring: 'ring-purple-500/20' },
  orange:  { bg: 'bg-orange-500',  text: 'text-orange-600 dark:text-orange-400',   dot: 'bg-orange-400',  border: 'border-orange-500',  ring: 'ring-orange-500/20' },
  emerald: { bg: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-400', border: 'border-emerald-500', ring: 'ring-emerald-500/20' },
  rose:    { bg: 'bg-rose-500',    text: 'text-rose-600 dark:text-rose-400',       dot: 'bg-rose-400',    border: 'border-rose-500',    ring: 'ring-rose-500/20' },
  amber:   { bg: 'bg-amber-500',   text: 'text-amber-600 dark:text-amber-400',     dot: 'bg-amber-400',   border: 'border-amber-500',   ring: 'ring-amber-500/20' },
  cyan:    { bg: 'bg-cyan-500',    text: 'text-cyan-600 dark:text-cyan-400',       dot: 'bg-cyan-400',    border: 'border-cyan-500',    ring: 'ring-cyan-500/20' },
  indigo:  { bg: 'bg-indigo-500',  text: 'text-indigo-600 dark:text-indigo-400',   dot: 'bg-indigo-400',  border: 'border-indigo-500',  ring: 'ring-indigo-500/20' },
  pink:    { bg: 'bg-pink-500',    text: 'text-pink-600 dark:text-pink-400',       dot: 'bg-pink-400',    border: 'border-pink-500',    ring: 'ring-pink-500/20' },
  lime:    { bg: 'bg-lime-500',    text: 'text-lime-600 dark:text-lime-400',       dot: 'bg-lime-400',    border: 'border-lime-500',    ring: 'ring-lime-500/20' },
  sky:     { bg: 'bg-sky-500',     text: 'text-sky-600 dark:text-sky-400',         dot: 'bg-sky-400',     border: 'border-sky-500',     ring: 'ring-sky-500/20' },
  slate:   { bg: 'bg-slate-500',   text: 'text-slate-600 dark:text-slate-400',     dot: 'bg-slate-400',   border: 'border-slate-500',   ring: 'ring-slate-500/20' },
}
